/**
 * Which screen, for which surface, for this person.
 *
 * Navigation is built from what the signed-in account may actually do, so a
 * link is never an invitation to a 403. The server checks the same thing on
 * every request; this only decides what to draw.
 */
import type { ReactNode } from "react";
import type { Area } from "@dp/shared";
import { Layout, type NavItem } from "./components/Layout.tsx";
import { Loading, NotPermitted } from "./components/Page.tsx";
import { SignIn } from "./pages/SignIn.tsx";
import { Guide } from "./pages/Guide.tsx";
import { ConfigOverview } from "./pages/config/Overview.tsx";
import { Entities } from "./pages/config/Entities.tsx";
import { Domains } from "./pages/config/Domains.tsx";
import { Fields } from "./pages/config/Fields.tsx";
import { FieldEditor } from "./pages/config/FieldEditor.tsx";
import { Products } from "./pages/config/Products.tsx";
import { ProductBuilder } from "./pages/config/ProductBuilder.tsx";
import { PortalOverview } from "./pages/portal/Overview.tsx";
import { Catalogue } from "./pages/portal/Catalogue.tsx";
import { Applications } from "./pages/portal/Applications.tsx";
import { ApplicationEditor } from "./pages/portal/ApplicationEditor.tsx";
import { Accounts } from "./pages/accounts/Accounts.tsx";
import { AccountEditor } from "./pages/accounts/AccountEditor.tsx";
import { MyAccount } from "./pages/accounts/MyAccount.tsx";
import { useAuth } from "./lib/auth.tsx";
import { match, useRouter } from "./lib/router.tsx";
import { surface } from "./lib/surface.ts";

export function App() {
  const { user, loading, can } = useAuth();
  const { path } = useRouter();

  if (loading) return <Loading what="your session" />;
  if (!user) return <SignIn />;

  const nav: NavItem[] = surface === "config"
    ? [
        { to: "/", label: "Overview" },
        ...(can("configuration") ? [
          { to: "/fields", label: "Field library" },
          { to: "/entities", label: "Entities" },
          { to: "/domains", label: "Value domains" },
        ] : []),
        ...(can("products") ? [{ to: "/products", label: "Products" }] : []),
        ...(can("accounts") ? [{ to: "/accounts", label: "Accounts" }] : []),
        { to: "/guide", label: "Walkthrough" },
        { to: "/me", label: "Your account" },
      ]
    : [
        { to: "/", label: "Overview" },
        ...(can("applications") ? [{ to: "/applications", label: "Applications" }] : []),
        ...(can("products") ? [{ to: "/catalogue", label: "Product catalogue" }] : []),
        ...(can("accounts") ? [{ to: "/accounts", label: "Accounts" }] : []),
        { to: "/guide", label: "Walkthrough" },
        { to: "/me", label: "Your account" },
      ];

  /** Refuse the screen, not just the request it would have made. */
  const guarded = (area: Area, what: string, element: ReactNode): ReactNode =>
    can(area) ? element : <NotPermitted what={what} />;

  return <Layout nav={nav}>{screen(path, surface === "config", guarded)}</Layout>;
}

type Guarded = (area: Area, what: string, element: ReactNode) => ReactNode;

function screen(path: string, isConfig: boolean, guarded: Guarded): ReactNode {
  // Both surfaces carry these: your own account, and accounts management for
  // whoever holds it.
  if (match("/guide", path)) return <Guide />;
  if (match("/me", path)) return <MyAccount />;
  if (match("/accounts", path)) return guarded("accounts", "accounts", <Accounts />);
  const account = match("/accounts/:id", path);
  if (account?.id) return guarded("accounts", "accounts", <AccountEditor id={account.id} />);

  return isConfig ? configScreen(path, guarded) : portalScreen(path, guarded);
}

function configScreen(path: string, guarded: Guarded): ReactNode {
  const library = (element: ReactNode) => guarded("configuration", "the field library", element);
  const products = (element: ReactNode) => guarded("products", "products", element);

  if (match("/", path)) return <ConfigOverview />;
  if (match("/entities", path)) return library(<Entities />);
  if (match("/domains", path)) return library(<Domains />);
  if (match("/fields", path)) return library(<Fields />);
  if (match("/fields/new", path)) return library(<FieldEditor />);

  const field = match("/fields/:key", path);
  if (field?.key) return library(<FieldEditor fieldKey={field.key} />);

  if (match("/products", path)) return products(<Products />);
  const product = match("/products/:code", path);
  if (product?.code) return products(<ProductBuilder code={product.code} />);

  return <NotFound path={path} />;
}

function portalScreen(path: string, guarded: Guarded): ReactNode {
  if (match("/", path)) return <PortalOverview />;
  if (match("/applications", path)) return guarded("applications", "applications", <Applications />);
  if (match("/catalogue", path)) return guarded("products", "the product catalogue", <Catalogue />);

  const application = match("/applications/:id", path);
  if (application?.id) return guarded("applications", "applications", <ApplicationEditor id={application.id} />);

  return <NotFound path={path} />;
}

function NotFound({ path }: { path: string }) {
  return (
    <div className="stack">
      <h1>Nothing here</h1>
      <div className="card">
        <div className="empty">
          <h3>No screen at this address</h3>
          <p className="small key">{path}</p>
        </div>
      </div>
    </div>
  );
}
